import { OFFLINE_STORES } from './offlineStorageAdapter.js';
import { conflictSnapshotContent, isResolvedConflict } from './offlineConflictIdentity.js';
import {legacyFileNeedsRecovery, legacySourceFingerprint, unpackLegacyQueue} from './legacyRecoverySources.js';
import {verifiedLegacyRecoveryCoverage} from './legacyVisitRecovery.js';

const SOURCES = [
    ['coordinacion_visitas_offline', [OFFLINE_STORES.ENTRIES, OFFLINE_STORES.OPERATIONS, OFFLINE_STORES.MEDIA,
        OFFLINE_STORES.GPS, OFFLINE_STORES.SHIFTS, OFFLINE_STORES.RRHH, OFFLINE_STORES.RRHH_FILES, OFFLINE_STORES.FILES]],
    ['coordinacion_visitas_offline_operations', ['visita_offline_operations']],
    ['coordinacion_visitas_offline_media', ['visita_offline_media_entries']],
];
const LOCAL_QUEUES = ['react_visitas_offline_queue_v1', 'react_visitas_offline_queue_v2_fallback'];
const completed = new Set(['applied', 'superseded', 'sent', 'synced', 'resolved_discarded', 'resolved_reapplied', 'acknowledged', 'discarded']);

function countRecord(row, counts) {
    if (!row || typeof row !== 'object') throw new Error('Formato de respaldo desconocido.');
    // Contextos/cache anteriores no son respuestas pendientes ni conceden autorización.
    if (/^context:\d+$/.test(String(row.key || ''))) return;
    let payload = row.payload;
    if (typeof payload === 'string') {
        try { payload = JSON.parse(payload); } catch { counts.corruptRecords++; }
    }
    const status = row.status || payload?.status || row.state || payload?.state;
    if (counts.source.endsWith(`/${OFFLINE_STORES.FILES}`)) {
        if (!legacyFileNeedsRecovery(row)) return;
    } else if (completed.has(status)) return;
    counts.pending++;
    const owner = Number(row.userId ?? row.user_id ?? payload?.userId);
    if (!Number.isSafeInteger(owner) || owner < 1
        || (payload?.userId !== undefined && Number(payload.userId) !== owner)) counts.ambiguousOwners++;
}

const emptyCounts = () => ({ pending: 0, ambiguousOwners: 0, corruptRecords: 0 });

async function inspectDatabase(factory, name, stores, timeoutMs, covered) {
    if (!factory?.open) return [{ source: name, status: 'unreadable', pending: null }];
    return new Promise(resolve => {
        let settled = false, missing = false, db, transaction;
        const finish = result => {
            if (settled) return;
            settled = true; clearTimeout(timer); db?.close(); resolve(result);
        };
        const timer = setTimeout(() => {
            try { transaction?.abort(); } catch { /* Solo se aborta una lectura. */ }
            finish([{ source: name, status: 'unreadable', pending: null }]);
        }, timeoutMs);
        try {
            const open = factory.open(name);
            open.onupgradeneeded = () => {
                missing = true;
                open.transaction.abort();
                open.result?.close();
            };
            open.onerror = () => finish([{ source: name, status: missing ? 'absent' : 'unreadable', pending: missing ? 0 : null }]);
            open.onblocked = () => finish([{ source: name, status: 'unreadable', pending: null }]);
            open.onsuccess = () => {
                db = open.result;
                if (settled) { db.close(); return; }
                const existing = stores.filter(store => db.objectStoreNames.contains(store));
                const results = stores.map(store => ({ source: `${name}/${store}`, status: existing.includes(store) ? 'present' : 'absent', ...emptyCounts() }));
                if (!existing.length) { finish(results); return; }
                try {
                    transaction = db.transaction(existing, 'readonly');
                    transaction.oncomplete = () => {
                        clearTimeout(timer); db.close();
                        void (async () => {
                            for (const counts of results) {
                                for (const row of counts.rows || []) {
                                    if (covered.has(await legacySourceFingerprint(counts.source, row))) counts.recovered = (counts.recovered || 0) + 1;
                                    else countRecord(row, counts);
                                }
                                delete counts.rows;
                            }
                            finish(results);
                        })().catch(() => finish([{source: name, status: 'unreadable', pending: null}]));
                    };
                    transaction.onerror = transaction.onabort = () => finish([{ source: name, status: 'unreadable', pending: null }]);
                    for (const store of existing) {
                        const counts = results.find(item => item.source === `${name}/${store}`);
                        const cursor = transaction.objectStore(store).openCursor();
                        cursor.onsuccess = () => {
                            const row = cursor.result;
                            if (!row) return;
                            try { (counts.rows ||= []).push(row.value); row.continue(); }
                            catch { transaction.abort(); }
                        };
                    }
                } catch { finish([{ source: name, status: 'unreadable', pending: null }]); }
            };
        } catch { finish([{ source: name, status: 'unreadable', pending: null }]); }
    });
}

async function inspectLocal(local, driver, covered) {
    if (!local) return [{ source: 'localStorage', status: 'unreadable', pending: null }];
    const results = [];
    try {
        if (!Number.isSafeInteger(local.length) || local.length < 0 || typeof local.key !== 'function') throw new Error('Inventario no disponible.');
        const keys = [...LOCAL_QUEUES];
        for (let n = 0; n < local.length; n++) {
            const key = local.key(n);
            if (/^visita_conflict_(?:\d+_)?\d+(?::[A-Za-z0-9_-]+)?$/.test(key || '')) keys.push(key);
        }
        for (const key of keys) {
            const counts = { source: key, status: 'absent', ...emptyCounts() }, raw = local.getItem(key);
            if (raw === null || raw === undefined) { results.push(counts); continue; }
            try {
                const parsed = JSON.parse(raw);
                const mirror = key.startsWith('visita_conflict_');
                const rows = mirror ? [parsed] : unpackLegacyQueue(parsed);
                if (!rows) throw new Error('Formato de respaldo desconocido.');
                counts.status = 'present';
                for (const row of rows) {
                    if (!mirror && covered.has(await legacySourceFingerprint(key, row))) {counts.recovered = (counts.recovered || 0) + 1; continue;}
                    // Un espejo ya confirmado en la autoridad no es una segunda intención.
                    if (mirror && driver && Number.isSafeInteger(Number(row?.userId)) && Number(row.userId) > 0) {
                        const root = `conflict:${row.userId}:${row.visitaId}`;
                        const targetKey = row.key === root || row.key?.startsWith(`${root}:`) ? row.key : root;
                        const target = await driver.get(OFFLINE_STORES.ENTRIES, targetKey);
                        if (target?.userId === Number(row.userId) && JSON.stringify(target.payload) === JSON.stringify(row)) continue;
                        if (target?.userId === Number(row.userId) && target.payload?.revisionId === row.revisionId
                            && isResolvedConflict(target.payload)
                            && target.payload.resolution?.resolvedBy === Number(row.userId)) {
                            if (conflictSnapshotContent(row) === conflictSnapshotContent(target.payload)) continue;
                        }
                    }
                    countRecord(row, counts);
                }
                results.push(counts);
            } catch { results.push({ source: key, status: 'unreadable', pending: null }); }
        }
    } catch { return [{ source: 'localStorage', status: 'unreadable', pending: null }]; }
    return results;
}

// Inventario exclusivamente de lectura: no migra, atribuye propietarios ni retira orígenes.
// Un error/timeout es desconocido, nunca una fuente vacía ni una migración completada.
export async function inspectLegacyOfflineInventory({ idb = typeof window === 'undefined' ? null : window.indexedDB,
    local = typeof window === 'undefined' ? null : window.localStorage, driver = null, fs, timeoutMs = 10000 } = {}) {
    const covered = driver ? await verifiedLegacyRecoveryCoverage({driver, fs}) : new Set();
    const groups = await Promise.all([...SOURCES.map(([name, stores]) => inspectDatabase(idb, name, stores, timeoutMs, covered)), inspectLocal(local, driver, covered)]);
    const sources = groups.flat(), complete = sources.every(item => item.status !== 'unreadable' && !item.corruptRecords);
    const knownPending = sources.reduce((total, item) => total + (item.pending || 0), 0);
    return { sources, complete, pending: complete ? knownPending : null, knownPending,
        ambiguousOwners: sources.reduce((total, item) => total + (item.ambiguousOwners || 0), 0),
        corruptRecords: sources.reduce((total, item) => total + (item.corruptRecords || 0), 0), preserved: true };
}
